import { useState, useCallback } from 'react';
import axios from 'axios';
import { downloadPdf } from '../utils/helpers';

const PDF_URL = process.env.REACT_APP_PDF_URL || 'https://pdf.relux.com';

export interface GeneratePdfParams {
  template: string;
  data: Record<string, any>;
}

export interface UsePdfReturn {
  pdf: Blob | null;
  loading: boolean;
  error: any;
  generatePdf: (params: GeneratePdfParams) => Promise<void>;
  downloadPdf: (fileName?: string) => void;
}

const useGeneratePdf = (): UsePdfReturn => {
  const [pdf, setPdf] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generatePdf = useCallback(async (params: GeneratePdfParams) => {
    const { template, data } = params;
    setLoading(true);
    setError(null);

    try {
      // Call the Carbone API to render the template
      // Send data directly without wrapping - the template expects d.xxx paths
      const response = await axios.post(
        `${PDF_URL}/render/${template}`,
        { data },
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      // Set the PDF blob
      setPdf(response.data);
    } catch (err) {
      setError((err as any).message || 'Failed to generate PDF');
      setPdf(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDownloadPdf = useCallback(
    (fileName = 'report.pdf') => {
      if (pdf) {
        downloadPdf(pdf, fileName);
      }
    },
    [pdf]
  );

  return { pdf, loading, error, generatePdf, downloadPdf: handleDownloadPdf };
};

export default useGeneratePdf;
